import React from 'react';
import {FormattedMessage} from 'react-intl';

import {helper} from '@stubhub/react-cloudinary';

import {NAMED_TRANSFORMATIONS} from '../../utils/cloudinaryConfigs';
const {THUMB_VFS, VFS} = NAMED_TRANSFORMATIONS;

/**
 * Get VFSImageUrl
 * @param  {Number} venueId
 * @param  {[Number]} venueConfigId
 * @param  {Number} venueConfigSectionId
 * @param  {boolean} isThumb
 * @return {String} vrImage
 */
export const getVFSImageUrl = ({venueId, venueConfigId, sectionId, isThumb = false}) => {
  const isValid = venueId && venueConfigId && sectionId;
  let vrImage;
  if (isValid) {
    vrImage = helper
      .getCloudinaryImage({
        publicIdSrc: `maps/sectionviews/venues/${venueId}/config/${venueConfigId}/default/${sectionId}`,
        namedTransformation: isThumb ? THUMB_VFS : VFS,
      })
      .toURL();
  }

  return vrImage;
};

export const getListingRow = (seats = []) => {
  const row = seats && seats[0] && seats[0].row;
  if (row === 'General Admission') {
    return <FormattedMessage id="row.generalAdmission" defaultMessage="GA" />;
  }

  return row;
};

const isSeatNumberIsConsecutive = (seatsUnsorted) => {
  const seats = [...seatsUnsorted];
  // Do not mutate initial array to avoid side effects
  seats.sort((a, b) => Number(a.seatNumber) - Number(b.seatNumber));
  let isConsecutive = true;
  const seatNumberArray = [];
  let prevNumber = Number(seats[0].seatNumber);
  seatNumberArray.push(prevNumber);
  for (let i = 1; i < seats.length; i++) {
    const nextNumber = Number(seats[i].seatNumber);
    if (nextNumber !== prevNumber + 1) {
      isConsecutive = false;
    }
    prevNumber = nextNumber;
    seatNumberArray.push(nextNumber);
  }

  return {isConsecutive, seatNumberArray};
};

export const getListingSeats = (seats = []) => {
  const seatNumber = seats && seats[0] && seats[0].seatNumber;
  if (seats.length === 0 || isNaN(seatNumber)) {
    if (seatNumber === 'General Admission' || seatNumber === 'GA') {
      return <FormattedMessage id="seat.generalAdmission" defaultMessage="GA" />;
    }

    return;
  }
  if (seats.length === 1) {
    return seats && seats[0] && seats[0].seatNumber;
  }
  const {isConsecutive, seatNumberArray} = isSeatNumberIsConsecutive(seats);
  if (isConsecutive) {
    return `${seatNumberArray[0]}-${seatNumberArray[seatNumberArray.length - 1]}`;
  }

  return seatNumberArray.join(', ');
};

export const getListingId = (seats = []) => {
  return seats && seats[0] && seats[0].listingId;
};

export const getEntryByKeyValue = (collection = [], key = '', value = '') => {
  return collection.find((entry) => entry[key].toString() === value.toString());
};

const SECTION_NAME_ADITIONAL_MAX_LENTGTH = 40;
const SECTION_NAME_MAX_LENGTH = 10;

/**
 * This method returns Header Title both for single section ID and for array of sections
 * @param  {Number | Array} selectedSectionId [description]
 * @param  {Array}  [sectionStats=[]] [description]
 * @param  {string}  screenSize - limit of section names in title (probably for desktop should be more than 2)
 * @param  {Number}  screenWidth - we're passing it directly from selector to get all things consistent
 */
export const getSectionName = (selectedSectionId, sectionStats = [], screenSize, screenWidth) => {
  if (Array.isArray(selectedSectionId)) {
    const sectionsNames = selectedSectionId.map(
      (sectionId) => (getEntryByKeyValue(sectionStats, 'sectionId', sectionId) || {}).sectionName
    );
    let [title] = sectionsNames;
    const limit = screenSize === 'small' ? 2 : 3;
    for (let i = 1; i < limit; i++) {
      if (!sectionsNames[i]) {
        continue;
      }
      title += `, ${sectionsNames[i]}`;
    }
    const additionalSections = sectionsNames.length > limit ? `... +${sectionsNames.length - limit}` : null;
    const textLengthLimit = Math.floor(
      (screenWidth - (additionalSections ? SECTION_NAME_ADITIONAL_MAX_LENTGTH : SECTION_NAME_MAX_LENGTH)) /
        SECTION_NAME_MAX_LENGTH
    );
    if (title.length > textLengthLimit) {
      title = title.substring(0, textLengthLimit).trim();
    }
    if (additionalSections) {
      title += additionalSections;
    }

    return title && title.length > 0 ? title : null;
  }

  return (getEntryByKeyValue(sectionStats, 'sectionId', selectedSectionId) || {}).sectionName;
};

export const getZoneName = (selectedZoneId, zoneStats = []) => {
  return (getEntryByKeyValue(zoneStats, 'zoneId', selectedZoneId) || {}).zoneName;
};

/**
 * Satisfies https://jira.stubcorp.com/browse/GCG-1125
 * Satisfies https://jira.stubcorp.com/browse/GCG-1444
 * Given a split vector, makes a decision on what the default quantity suggestion should be for a listing.
 * If default quantity preference is contained in split vector return it, otherwise return the lowest available quantity for a given eventType. If the eventType is Parking, the default should be 1. Otherwise, the default should be 2.
 * @param listing - object referencing a listing that may contain a `splitVector` prop
 * @param suggestedQuantity - the desired quantity to set if possible
 * @param eventAttributes = object that includes data on the eventType.
 * @returns {number} default quantity that should be selected for a listing
 */
export const getDefaultQuantityBasedOnListing = ({listing = {}, suggestedQuantity, eventAttributes = {}}) => {
  const DEFAULT_TICKET_QUANTITY_PREFERENCE = 2;
  const DEFAULT_TICKET_QUANTITY_PREFERENCE_PARKING = 1;

  const {eventType = ''} = eventAttributes;
  const {splitVector = []} = listing;

  if (splitVector.includes(suggestedQuantity)) {
    return suggestedQuantity;
  }
  if (eventType === 'Parking' && splitVector.includes(DEFAULT_TICKET_QUANTITY_PREFERENCE_PARKING)) {
    return DEFAULT_TICKET_QUANTITY_PREFERENCE_PARKING;
  }
  if (splitVector.includes(DEFAULT_TICKET_QUANTITY_PREFERENCE)) {
    return DEFAULT_TICKET_QUANTITY_PREFERENCE;
  }

  return splitVector[0];
};
